import * as React from 'react';

import { HistoryContext, LocationContext } from './contexts';
import { createHistory, type NavigationHistory } from './createHistory';

/**
 * Provide the history controller and the current location to the react tree.
 * See: `useHistory` and `useHistoryLocation` to access the history and location.
 */
export function HistoryProvider(props: {
    history?: NavigationHistory;
    children: React.ReactNode;
}): React.ReactElement {
    const { history: propHistory, children } = props;

    const history = React.useMemo(() => {
        return propHistory || createHistory();
    }, [propHistory]);

    const subscription = React.useMemo(() => {
        return {
            getSnapshot: () => history.location,
            subscribe: (callback: () => void) => {
                return history.listen(callback);
            },
        };
    }, [history]);

    const location = React.useSyncExternalStore(
        subscription.subscribe,
        subscription.getSnapshot,
        subscription.getSnapshot
    );

    return (
        <HistoryContext.Provider value={history}>
            <LocationContext.Provider value={location}>{children}</LocationContext.Provider>
        </HistoryContext.Provider>
    );
}
